"use client";

import { SettingsRow, SettingsSection } from "@commerce/components/page";
import {
	dateLabel,
	fullTime,
	Time,
	useNow,
	useTimeZone,
	when,
} from "@commerce/components/time";
import { SecretDialog } from "@commerce/integrations/SecretDialog";
import {
	type ApiScope,
	type ApiScopeOption,
	CIDR_HINT,
	MAX_ALLOWED_IPS,
	normalizeCidr,
	ORG_SCOPES,
} from "@repo/api/modules/api-keys/types";
import { SettingsPageIntro } from "@saas/settings/components/SettingsLayout";
import { errorMessage } from "@saas/settings/lib/errors";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { orpc } from "@shared/lib/orpc";
import { useForm, useStore } from "@tanstack/react-form";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@ui/components/collapsible";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { FieldError } from "@ui/components/field";
import { DisabledReason, InfoTip } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@ui/components/input-group";
import { AnchorLinkButton } from "@ui/components/link-button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@ui/components/sheet";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ui/components/table";
import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import { formatDistanceStrict, formatDistanceToNowStrict } from "date-fns";
import {
	ChevronRightIcon,
	ExternalLinkIcon,
	KeyRoundIcon,
	RefreshCwIcon,
	XIcon,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { z } from "zod";
import { apiKeysQueryOptions } from "../hooks/use-api-keys";

const SURFACE =
	"overflow-hidden rounded-3xl bg-card ring-1 ring-foreground/6 dark:ring-foreground/10";
const HOUR_MS = 3_600_000;
/** How long a rotated key keeps working, so the integration can switch without downtime. */
const ROTATION_OVERLAP_HOURS = 24;

const EVERYTHING = ORG_SCOPES.filter((s) => s.scope === "*");
const SCOPE_GROUPS = [
	...new Set(ORG_SCOPES.filter((s) => s.scope !== "*").map((s) => s.group)),
];
const SCOPE_LABEL = new Map<string, string>(
	ORG_SCOPES.map((s) => [s.scope, s.label]),
);

const EXPIRY = {
	never: "Never",
	"30": "In 30 days",
	"90": "In 90 days",
	"365": "In a year",
} as const;
type Expiry = keyof typeof EXPIRY;

const nameSchema = z
	.string()
	.trim()
	.min(2, "Give the key a name, so you know what breaks if you revoke it")
	.max(100, "Keep the name under 100 characters");
const scopesSchema = z.array(z.string()).min(1, "Pick at least one scope");

type ApiKeyRow = Awaited<
	ReturnType<
		typeof import("@shared/lib/orpc")["orpcClient"]["apiKeys"]["list"]
	>
>["apiKeys"][number];

interface RevealedKey {
	title: string;
	key: string;
	/** One line under the key, for what the person must do next. */
	note?: string;
}

export function ApiKeysSettings({
	organizationId,
}: {
	organizationId: string;
}) {
	const [revealed, setRevealed] = useState<RevealedKey | null>(null);

	return (
		<>
			<SettingsPageIntro
				title="API keys"
				description="Keep keys on your server, never inside an app or web page."
				info="Your servers call the Hephon API with a key. Each key can only do what its scopes allow, optionally only from your own IP addresses."
			/>
			<CreateKeySection
				organizationId={organizationId}
				onCreated={setRevealed}
			/>
			<KeysSection
				organizationId={organizationId}
				onRotated={setRevealed}
			/>
			<UsageHelp />
			<SecretDialog
				revealed={
					revealed && {
						title: revealed.title,
						label: "API key",
						secret: revealed.key,
						note:
							revealed.note ??
							"Lost it? Revoke it and create a new one.",
					}
				}
				onClose={() => setRevealed(null)}
			/>
		</>
	);
}

export function ApiKeysSettingsSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<Skeleton className="h-10 w-2/3" />
			<Skeleton className="h-96 w-full rounded-3xl" />
			<Skeleton className="h-32 w-full rounded-3xl" />
		</div>
	);
}

function CreateKeySection({
	organizationId,
	onCreated,
}: {
	organizationId: string;
	onCreated: (revealed: RevealedKey) => void;
}) {
	const queryClient = useQueryClient();
	const create = useMutation(orpc.apiKeys.create.mutationOptions());
	const [ipDraft, setIpDraft] = useState("");
	const [ipBlocked, setIpBlocked] = useState(false);
	const form = useForm({
		defaultValues: {
			name: "",
			scopes: ["chat:write"] as string[],
			allowedIps: [] as string[],
			expiry: "never" as Expiry,
		},
		onSubmit: async ({ value, formApi }) => {
			try {
				const name = value.name.trim();
				const result = await create.mutateAsync({
					organizationId,
					name,
					scopes: value.scopes as ApiScope[],
					allowedIps: value.allowedIps,
					...(value.expiry === "never"
						? {}
						: {
								expiresAt: new Date(
									Date.now() +
										Number(value.expiry) * 24 * HOUR_MS,
								).toISOString(),
							}),
				});
				await queryClient.invalidateQueries({
					queryKey: orpc.apiKeys.list.key(),
				});
				formApi.reset();
				setIpDraft("");
				toast.add({ type: "success", title: `“${name}” created` });
				onCreated({ title: "Copy your new key", key: result.key });
			} catch (error) {
				toast.add({
					type: "error",
					title: "Could not create the key",
					description: errorMessage(error),
				});
			}
		},
	});
	const [canSubmit, isSubmitting, submitted] = useStore(
		form.store,
		(state) => [
			state.canSubmit,
			state.isSubmitting,
			state.submissionAttempts > 0,
		],
	);

	const submit = () => {
		// A typed address that was never added would silently leave the key open to
		// every address: it must be added or cleared first.
		if (ipDraftError(ipDraft, form.state.values.allowedIps)) {
			setIpBlocked(true);
			document.getElementById("api-key-ips")?.focus();
			return;
		}
		void form.handleSubmit();
	};

	return (
		<SettingsSection
			title="Create a key"
			footer={
				<DisabledReason
					reason={
						!isSubmitting &&
						!canSubmit &&
						"Fix the fields marked in red"
					}
				>
					<Button
						type="submit"
						form="create-api-key"
						disabled={isSubmitting || !canSubmit}
					>
						{isSubmitting ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Create key
					</Button>
				</DisabledReason>
			}
		>
			<form
				id="create-api-key"
				noValidate
				className="contents"
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					submit();
				}}
			>
				{/* Validated on change and shown from the first blur, so fixing the name clears the
				    error while typing, not on blur (a shift on blur ate the next click). */}
				<form.Field name="name" validators={{ onChange: nameSchema }}>
					{(field) => {
						const error =
							field.state.meta.isBlurred || submitted
								? field.state.meta.errors
								: [];
						return (
							<SettingsRow
								label="Name"
								htmlFor="api-key-name"
								info="Where the key is used, so you know what breaks if you revoke it."
							>
								<Input
									id="api-key-name"
									placeholder="Suppy backend, production"
									value={field.state.value}
									onChange={(event) =>
										field.handleChange(event.target.value)
									}
									onBlur={() => {
										field.handleBlur();
										void field.validate("change");
									}}
									maxLength={100}
									aria-invalid={error.length > 0 || undefined}
								/>
								{error.length > 0 ? (
									<FieldError
										errors={error as { message?: string }[]}
									/>
								) : null}
							</SettingsRow>
						);
					}}
				</form.Field>
				<form.Field
					name="scopes"
					validators={{
						onChange: scopesSchema,
						onSubmit: scopesSchema,
					}}
				>
					{(field) => (
						<SettingsRow
							label="Scopes"
							info="What the key may do. Give each integration only what it needs: a key that only pushes the catalog cannot chat."
						>
							<ScopePicker
								value={field.state.value}
								onChange={field.handleChange}
							/>
							{field.state.meta.errors.length > 0 ? (
								<FieldError
									errors={
										field.state.meta.errors as {
											message?: string;
										}[]
									}
								/>
							) : null}
						</SettingsRow>
					)}
				</form.Field>
				<form.Field name="allowedIps">
					{(field) => (
						<SettingsRow
							label="IP allow-list"
							htmlFor="api-key-ips"
							info="Calls from any other address are refused. Leave it empty to allow every address."
						>
							<IpAllowListEditor
								id="api-key-ips"
								value={field.state.value}
								onChange={field.handleChange}
								draft={ipDraft}
								onDraftChange={(next) => {
									setIpDraft(next);
									setIpBlocked(false);
								}}
								blocked={ipBlocked}
							/>
						</SettingsRow>
					)}
				</form.Field>
				<form.Field name="expiry">
					{(field) => (
						<SettingsRow label="Expires">
							<Select
								items={EXPIRY}
								value={field.state.value}
								onValueChange={(value) =>
									field.handleChange(value as Expiry)
								}
							>
								<SelectTrigger
									aria-label="Expires"
									className="w-full"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Object.entries(EXPIRY).map(
										([value, label]) => (
											<SelectItem
												key={value}
												value={value}
											>
												{label}
											</SelectItem>
										),
									)}
								</SelectContent>
							</Select>
						</SettingsRow>
					)}
				</form.Field>
			</form>
		</SettingsSection>
	);
}

/** Checkboxes grouped by resource. "Everything" covers the others, which then show as granted. */
function ScopePicker({
	value,
	onChange,
}: {
	value: string[];
	onChange: (next: string[]) => void;
}) {
	const id = useId();
	const everything = value.includes("*");
	const toggle = (scope: string, on: boolean) =>
		onChange(
			scope === "*"
				? on
					? ["*"]
					: []
				: on
					? [...value, scope]
					: value.filter((s) => s !== scope),
		);

	const option = (scope: ApiScopeOption) => (
		<li key={scope.scope} className="flex items-center gap-2">
			<Checkbox
				id={`${id}-${scope.scope}`}
				checked={everything || value.includes(scope.scope)}
				disabled={everything && scope.scope !== "*"}
				onCheckedChange={(checked) => toggle(scope.scope, checked)}
			/>
			<label
				htmlFor={`${id}-${scope.scope}`}
				className="text-sm has-disabled:opacity-50 peer-data-disabled:opacity-50"
			>
				{scope.label}
			</label>
			<InfoTip label={`About ${scope.label}`}>
				{scope.hint}{" "}
				<code className="font-mono text-xs">{scope.scope}</code>
			</InfoTip>
		</li>
	);

	return (
		<div className="flex w-full flex-col gap-4">
			{SCOPE_GROUPS.map((group) => (
				<fieldset key={group} className="flex flex-col gap-2">
					<legend className="mb-2 text-muted-foreground text-xs">
						{group}
					</legend>
					<ul className="flex flex-col gap-2">
						{ORG_SCOPES.filter(
							(s) => s.group === group && s.scope !== "*",
						).map(option)}
					</ul>
				</fieldset>
			))}
			<ul className="border-border/70 border-t pt-3">
				{EVERYTHING.map(option)}
			</ul>
		</div>
	);
}

/**
 * Why a typed, not yet added allow-list entry cannot be added (or that it still has to be);
 * null when nothing is typed.
 */
function ipDraftError(draft: string, value: string[]): string | null {
	return draft.trim() ? checkIpDraft(draft, value).error : null;
}

function checkIpDraft(
	draft: string,
	value: string[],
): { normalized: string; error: string } | { normalized: null; error: string } {
	const normalized = normalizeCidr(draft);
	if (!normalized) {
		return { normalized: null, error: CIDR_HINT };
	}
	if (value.includes(normalized)) {
		return {
			normalized: null,
			error: `${normalized} is already on the list`,
		};
	}
	if (value.length >= MAX_ALLOWED_IPS) {
		return {
			normalized: null,
			error: `Up to ${MAX_ALLOWED_IPS} entries. Use a range to cover more.`,
		};
	}
	return {
		normalized,
		error: "Press Enter to add this address, or clear it",
	};
}

/**
 * Addresses and ranges as removable chips, plus an input that validates before adding. The
 * typed draft belongs to the parent, so saving can refuse while one is left over (`blocked`
 * shows why).
 */
function IpAllowListEditor({
	id,
	value,
	onChange,
	draft,
	onDraftChange,
	blocked,
}: {
	id: string;
	value: string[];
	onChange: (next: string[]) => void;
	draft: string;
	onDraftChange: (next: string) => void;
	blocked: boolean;
}) {
	const [addError, setAddError] = useState<string | null>(null);
	const error = addError ?? (blocked ? ipDraftError(draft, value) : null);

	const add = () => {
		if (!draft.trim()) {
			return;
		}
		const checked = checkIpDraft(draft, value);
		if (!checked.normalized) {
			setAddError(checked.error);
			return;
		}
		onChange([...value, checked.normalized]);
		onDraftChange("");
		setAddError(null);
	};

	return (
		<div className="flex w-full flex-col gap-2">
			{value.length > 0 ? (
				<ul
					className="flex flex-wrap gap-1.5"
					aria-label="Allowed addresses"
				>
					{value.map((entry) => (
						<li key={entry}>
							<Badge
								variant="secondary"
								className="h-6 gap-1 pr-1 font-mono"
							>
								{entry}
								<button
									type="button"
									aria-label={`Remove ${entry}`}
									onClick={() =>
										onChange(
											value.filter((v) => v !== entry),
										)
									}
									className="rounded-full p-0.5 hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
								>
									<XIcon className="size-3" />
								</button>
							</Badge>
						</li>
					))}
				</ul>
			) : (
				<p className="text-muted-foreground text-xs">Any address</p>
			)}
			<InputGroup>
				<InputGroupInput
					id={id}
					placeholder="IP address or range"
					value={draft}
					className="font-mono placeholder:font-sans"
					aria-invalid={error ? true : undefined}
					aria-describedby={error ? `${id}-error` : undefined}
					onChange={(event) => {
						onDraftChange(event.target.value);
						setAddError(null);
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							// Enter adds the address; it never submits the surrounding form.
							event.preventDefault();
							add();
						}
					}}
					onBlur={add}
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						size="xs"
						onClick={add}
						disabled={!draft.trim()}
					>
						Add
					</InputGroupButton>
				</InputGroupAddon>
			</InputGroup>
			{error ? <FieldError id={`${id}-error`}>{error}</FieldError> : null}
		</div>
	);
}

function KeysSection({
	organizationId,
	onRotated,
}: {
	organizationId: string;
	onRotated: (revealed: RevealedKey) => void;
}) {
	const { data } = useSuspenseQuery(apiKeysQueryOptions(organizationId));
	const [managing, setManaging] = useState<string | null>(null);
	const keys = data.apiKeys;
	const current = keys.find((key) => key.id === managing) ?? null;

	if (keys.length === 0) {
		return (
			<Empty className="rounded-3xl border">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<KeyRoundIcon />
					</EmptyMedia>
					<EmptyTitle>No API keys yet</EmptyTitle>
					<EmptyDescription>
						Create one when your integration is ready.
					</EmptyDescription>
				</EmptyHeader>
				<EmptyContent>
					<Button
						variant="outline"
						onClick={() =>
							document.getElementById("api-key-name")?.focus()
						}
					>
						Create a key
					</Button>
				</EmptyContent>
			</Empty>
		);
	}

	const manage = (key: ApiKeyRow) => (
		<Button
			variant="outline"
			size="sm"
			aria-label={`Manage ${key.name}`}
			onClick={() => setManaging(key.id)}
		>
			Manage
		</Button>
	);

	return (
		<section
			className="flex flex-col gap-3"
			aria-labelledby="api-keys-title"
		>
			<h2
				id="api-keys-title"
				className="font-semibold text-base tracking-tight"
			>
				Keys{" "}
				<span className="font-normal text-muted-foreground tabular">
					{keys.length}
				</span>
			</h2>
			<div className={cn(SURFACE, "max-lg:hidden")}>
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead className="pl-5">Name</TableHead>
							<TableHead>Access</TableHead>
							<TableHead>Activity</TableHead>
							<TableHead className="pr-5">
								<span className="sr-only">Actions</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{keys.map((key) => (
							<TableRow
								key={key.id}
								className="align-top hover:bg-transparent"
							>
								<TableCell className="max-w-52 whitespace-normal py-3 pl-5">
									<p className="break-words font-medium">
										{key.name}
									</p>
									<KeyPrefix apiKey={key} />
								</TableCell>
								{/* What the key may do and from where, then its use and expiry: four
								    columns fit the settings width, six did not. */}
								<TableCell className="max-w-60 whitespace-normal py-3">
									<div className="flex flex-col items-start gap-1.5">
										<ScopeBadges scopes={key.scopes} />
										<IpSummary ips={key.allowedIps} />
									</div>
								</TableCell>
								<TableCell className="max-w-56 whitespace-normal py-3">
									<div className="flex flex-col items-start gap-1 text-muted-foreground">
										<LastUsed apiKey={key} />
										<ExpiryLabel
											expiresAt={key.expiresAt}
										/>
									</div>
								</TableCell>
								<TableCell className="py-3 pr-5 text-right">
									{manage(key)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<ul className={cn(SURFACE, "divide-y divide-border/70 lg:hidden")}>
				{keys.map((key) => (
					<li key={key.id} className="flex flex-col gap-2 px-4 py-3">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate font-medium text-sm">
									{key.name}
								</p>
								<KeyPrefix apiKey={key} />
							</div>
							{manage(key)}
						</div>
						<ScopeBadges scopes={key.scopes} />
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
							<IpSummary ips={key.allowedIps} />
							<LastUsed apiKey={key} />
							<ExpiryLabel expiresAt={key.expiresAt} />
						</div>
					</li>
				))}
			</ul>
			<ManageKeySheet
				organizationId={organizationId}
				apiKey={current}
				onClose={() => setManaging(null)}
				onRotated={(revealed) => {
					setManaging(null);
					onRotated(revealed);
				}}
			/>
		</section>
	);
}

/** The key's prefix, and whether it was rotated (it only lives out its overlap). */
function KeyPrefix({ apiKey }: { apiKey: ApiKeyRow }) {
	return (
		<p className="text-muted-foreground text-xs">
			<span className="font-mono">{apiKey.keyPrefix}…</span>
			{apiKey.rotatedAt ? " · Rotated" : null}
		</p>
	);
}

function ScopeBadges({ scopes }: { scopes: string[] }) {
	// Legacy values kept for rolling deploys (write:chat) are not scopes anymore.
	const known = scopes.filter((scope) => SCOPE_LABEL.has(scope));
	if (known.length === 0) {
		return (
			<span className="inline-flex items-center gap-1">
				<Badge variant="warning">No scopes</Badge>
				<InfoTip label="About keys without scopes">
					This key was created before scopes and can no longer call
					the API. Create a new key with the scopes it needs, then
					revoke this one.
				</InfoTip>
			</span>
		);
	}
	return (
		<span className="flex flex-wrap gap-1">
			{known.map((scope) => (
				<Badge
					key={scope}
					variant={scope === "*" ? "warning" : "secondary"}
				>
					{SCOPE_LABEL.get(scope)}
				</Badge>
			))}
		</span>
	);
}

function IpSummary({ ips }: { ips: string[] }) {
	if (ips.length === 0) {
		return (
			<span className="text-muted-foreground text-sm max-lg:text-xs">
				Any address
			</span>
		);
	}
	return (
		<span className="font-mono text-sm max-lg:text-xs">
			{ips[0]}
			{ips.length > 1 ? (
				<span className="font-sans text-muted-foreground">
					{" "}
					+{ips.length - 1}
				</span>
			) : null}
		</span>
	);
}

function LastUsed({ apiKey }: { apiKey: ApiKeyRow }) {
	if (!apiKey.lastUsedAt) {
		return <span className="text-sm max-lg:text-xs">Never used</span>;
	}
	return (
		<span className="text-sm max-lg:text-xs">
			Last used{" "}
			<Time
				at={new Date(apiKey.lastUsedAt)}
				as="relative"
				label="Last used"
			/>
			{apiKey.lastUsedIp ? (
				<>
					{" from "}
					<span className="font-mono">{apiKey.lastUsedIp}</span>
				</>
			) : null}
		</span>
	);
}

/**
 * Formatted in the panel's timezone like every admin timestamp, so the server render and the
 * hydrating client agree. "Expires in 3 hours" depends on now, so it appears once the page is
 * live (`useNow` is 0 until then).
 */
function ExpiryLabel({ expiresAt }: { expiresAt: Date | string | null }) {
	const timeZone = useTimeZone();
	const now = useNow();
	if (!expiresAt) {
		return (
			<span className="text-muted-foreground text-sm max-lg:text-xs">
				Never expires
			</span>
		);
	}
	const at = new Date(expiresAt);
	const title = fullTime(at, timeZone);
	// Expired is the same on both sides, short of a key expiring between render and hydration.
	const left = at.getTime() - Date.now();
	if (left <= 0) {
		return (
			<Badge variant="destructive" title={title}>
				Expired
			</Badge>
		);
	}
	if (now && left < 24 * HOUR_MS) {
		return (
			<Badge variant="warning" title={title}>
				Expires in {formatDistanceToNowStrict(at)}
			</Badge>
		);
	}
	return (
		<span className="text-sm max-lg:text-xs" title={title}>
			Expires {dateLabel(at, timeZone, true)}
		</span>
	);
}

/** Rename, IP allow-list, rotate and revoke one key. */
function ManageKeySheet({
	organizationId,
	apiKey,
	onClose,
	onRotated,
}: {
	organizationId: string;
	apiKey: ApiKeyRow | null;
	onClose: () => void;
	onRotated: (revealed: RevealedKey) => void;
}) {
	return (
		<Sheet
			open={apiKey !== null}
			onOpenChange={(open) => !open && onClose()}
		>
			<SheetContent
				side="right"
				className="w-full gap-0 data-[side=right]:sm:max-w-xl"
			>
				{apiKey ? (
					<ManageKeyForm
						key={apiKey.id}
						organizationId={organizationId}
						apiKey={apiKey}
						onClose={onClose}
						onRotated={onRotated}
					/>
				) : null}
			</SheetContent>
		</Sheet>
	);
}

function ManageKeyForm({
	organizationId,
	apiKey,
	onClose,
	onRotated,
}: {
	organizationId: string;
	apiKey: ApiKeyRow;
	onClose: () => void;
	onRotated: (revealed: RevealedKey) => void;
}) {
	const queryClient = useQueryClient();
	const update = useMutation(orpc.apiKeys.update.mutationOptions());
	const [name, setName] = useState(apiKey.name);
	const [allowedIps, setAllowedIps] = useState(apiKey.allowedIps);
	const [ipDraft, setIpDraft] = useState("");
	const [ipBlocked, setIpBlocked] = useState(false);
	const nameError = nameSchema.safeParse(name).error?.issues[0]?.message;
	// A typed address counts: Save then says why it cannot go yet, instead of staying disabled.
	const dirty =
		name.trim() !== apiKey.name ||
		allowedIps.join() !== apiKey.allowedIps.join() ||
		ipDraft.trim() !== "";
	const listKey = orpc.apiKeys.list.key();

	const save = async () => {
		if (ipDraftError(ipDraft, allowedIps)) {
			setIpBlocked(true);
			document.getElementById("manage-key-ips")?.focus();
			return;
		}
		try {
			await update.mutateAsync({
				id: apiKey.id,
				name: name.trim(),
				allowedIps,
			});
			await queryClient.invalidateQueries({ queryKey: listKey });
			toast.add({ type: "success", title: "Key updated" });
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not update the key",
				description: errorMessage(error),
			});
		}
	};

	return (
		<>
			<SheetHeader className="border-border/70 border-b pr-14">
				<SheetTitle>{apiKey.name}</SheetTitle>
				<SheetDescription className="font-mono">
					{apiKey.keyPrefix}…
				</SheetDescription>
			</SheetHeader>
			<div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5 md:p-6">
				<div className="flex flex-col divide-y divide-border/70">
					<SettingsRow label="Name" htmlFor="manage-key-name">
						<Input
							id="manage-key-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							maxLength={100}
							aria-invalid={nameError ? true : undefined}
						/>
						{nameError ? (
							<FieldError>{nameError}</FieldError>
						) : null}
					</SettingsRow>
					<SettingsRow
						label="IP allow-list"
						htmlFor="manage-key-ips"
						info="Calls from any other address are refused. Leave it empty to allow every address."
					>
						<IpAllowListEditor
							id="manage-key-ips"
							value={allowedIps}
							onChange={setAllowedIps}
							draft={ipDraft}
							onDraftChange={(next) => {
								setIpDraft(next);
								setIpBlocked(false);
							}}
							blocked={ipBlocked}
						/>
					</SettingsRow>
					<SettingsRow
						label="Scopes"
						info="Scopes cannot change. Create a new key for different access."
					>
						<ScopeBadges scopes={apiKey.scopes} />
					</SettingsRow>
				</div>
				<KeyLifecycleActions
					organizationId={organizationId}
					apiKey={apiKey}
					onClose={onClose}
					onRotated={onRotated}
				/>
			</div>
			<SheetFooter className="flex-row justify-end gap-2 border-border/70 border-t">
				<Button variant="ghost" onClick={onClose}>
					Close
				</Button>
				<DisabledReason
					reason={
						!update.isPending &&
						(nameError
							? "Fix the name first"
							: !dirty && "Nothing changed yet")
					}
				>
					<Button
						onClick={save}
						disabled={
							update.isPending || !dirty || Boolean(nameError)
						}
					>
						{update.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Save changes
					</Button>
				</DisabledReason>
			</SheetFooter>
		</>
	);
}

/** Rotate and revoke: each confirms first and says when the old key stops working. */
function KeyLifecycleActions({
	organizationId,
	apiKey,
	onClose,
	onRotated,
}: {
	organizationId: string;
	apiKey: ApiKeyRow;
	onClose: () => void;
	onRotated: (revealed: RevealedKey) => void;
}) {
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();
	const rotate = useMutation(orpc.apiKeys.rotate.mutationOptions());
	const revoke = useMutation(orpc.apiKeys.revoke.mutationOptions());
	const expired = apiKey.expiresAt
		? new Date(apiKey.expiresAt).getTime() <= Date.now()
		: false;
	const rotated = apiKey.rotatedAt !== null;
	const listKey = orpc.apiKeys.list.key();

	const timeZone = useTimeZone();
	const until = (at: number | string | Date) =>
		when(new Date(at), new Date(), timeZone);
	// The old key keeps working for the overlap, but never past its own expiry.
	const overlapEnd = Math.min(
		Date.now() + ROTATION_OVERLAP_HOURS * HOUR_MS,
		apiKey.expiresAt
			? new Date(apiKey.expiresAt).getTime()
			: Number.POSITIVE_INFINITY,
	);

	const newKeyExpiry = apiKey.expiresAt
		? `The new key expires ${lifetimeLabel(apiKey.createdAt, apiKey.expiresAt)} after it is created, like this one.`
		: "The new key never expires, like this one.";
	const newKeyEnds = (expiresAt: Date | string | null) =>
		expiresAt
			? `The new key expires ${until(expiresAt)}.`
			: "The new key never expires.";

	const startRotate = () =>
		confirm({
			title: `Rotate “${apiKey.name}”?`,
			message: `You get a new key with the same scopes and IP allow-list. ${newKeyExpiry} The current key keeps working until ${until(overlapEnd)}, then stops.`,
			confirmLabel: "Rotate key",
			onConfirm: async () => {
				const result = await rotate.mutateAsync({
					id: apiKey.id,
					overlapHours: ROTATION_OVERLAP_HOURS,
				});
				await queryClient.invalidateQueries({ queryKey: listKey });
				const oldKeyEnds = until(result.previousKeyExpiresAt);
				toast.add({
					type: "success",
					title: `“${apiKey.name}” rotated`,
					description: `The old key works until ${oldKeyEnds}. ${newKeyEnds(result.expiresAt)}`,
				});
				onRotated({
					title: "Copy the new key",
					key: result.key,
					note: `Replace the old key before ${oldKeyEnds}. After that it stops working. ${newKeyEnds(result.expiresAt)}`,
				});
			},
		});

	const startRevoke = () =>
		confirm({
			title: `Revoke “${apiKey.name}”?`,
			message:
				"Anything using this key stops working immediately, including shopper chats in your app. This can't be undone.",
			confirmLabel: "Revoke key",
			destructive: true,
			onConfirm: async () => {
				await revoke.mutateAsync({ id: apiKey.id });
				queryClient.setQueryData(
					apiKeysQueryOptions(organizationId).queryKey,
					(previous) =>
						previous
							? {
									...previous,
									apiKeys: previous.apiKeys.filter(
										(k) => k.id !== apiKey.id,
									),
								}
							: previous,
				);
				onClose();
				await queryClient.invalidateQueries({ queryKey: listKey });
				toast.add({
					type: "success",
					title: `“${apiKey.name}” was revoked`,
				});
			},
		});

	return (
		<div className="flex flex-col gap-3 rounded-3xl border border-border/70 p-4">
			<ActionRow
				title="Rotate"
				detail={`New key, same access. The old one keeps working for ${ROTATION_OVERLAP_HOURS} hours.`}
			>
				<DisabledReason
					reason={
						expired
							? "This key has expired. Create a new one."
							: rotated &&
								"This key was already rotated. Rotate its replacement instead."
					}
				>
					<Button
						variant="outline"
						size="sm"
						onClick={startRotate}
						disabled={expired || rotated}
					>
						<RefreshCwIcon data-icon="inline-start" />
						Rotate
					</Button>
				</DisabledReason>
			</ActionRow>
			<ActionRow title="Revoke" detail="Stops the key immediately.">
				<Button variant="destructive" size="sm" onClick={startRevoke}>
					Revoke
				</Button>
			</ActionRow>
		</div>
	);
}

/** "30 days", "365 days": what an admin picked when creating the key, not "1 month". */
function lifetimeLabel(from: Date | string, to: Date | string): string {
	const start = new Date(from);
	const end = new Date(to);
	return formatDistanceStrict(start, end, {
		...(end.getTime() - start.getTime() >= 2 * 24 * HOUR_MS
			? { unit: "day" as const }
			: {}),
		roundingMethod: "round",
	});
}

function ActionRow({
	title,
	detail,
	children,
}: {
	title: string;
	detail: string;
	children: ReactNode;
}) {
	return (
		<div className="flex items-center justify-between gap-4">
			<div className="min-w-0">
				<p className="font-medium text-sm">{title}</p>
				<p className="text-muted-foreground text-xs">{detail}</p>
			</div>
			{children}
		</div>
	);
}

function UsageHelp() {
	return (
		<Collapsible className="flex flex-col gap-3">
			<CollapsibleTrigger
				render={
					<Button
						variant="ghost"
						size="sm"
						className="group w-fit text-muted-foreground"
					/>
				}
			>
				<ChevronRightIcon
					data-icon="inline-start"
					className="transition-transform group-data-panel-open:rotate-90"
				/>
				How to use a key
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className={cn(SURFACE, "flex flex-col gap-3 p-5 text-sm")}>
					<p className="text-muted-foreground">
						Send shopper messages from your server with a key that
						has the{" "}
						<code className="font-mono text-foreground">
							chat:write
						</code>{" "}
						scope. Replies stream back as server-sent events.
					</p>
					<pre className="overflow-x-auto rounded-2xl bg-muted/60 p-4 font-mono text-xs leading-relaxed">
						{`POST /api/v1/chat
Authorization: Bearer <your key>
Content-Type: application/json

{ "message": { "text": "3endkon halib?" }, "shopperId": "your-shopper-id" }`}
					</pre>
					<p className="text-muted-foreground">
						The conversation id comes back in the{" "}
						<code className="font-mono text-foreground">
							x-conversation-id
						</code>{" "}
						header. Send it as{" "}
						<code className="font-mono text-foreground">
							conversationId
						</code>{" "}
						to continue the same chat. Never send emails or phone
						numbers as the shopper id.
					</p>
					<AnchorLinkButton
						href="/api/v1/docs"
						target="_blank"
						rel="noreferrer"
						variant="outline"
						size="sm"
						className="w-fit"
					>
						API reference
						<ExternalLinkIcon data-icon="inline-end" />
					</AnchorLinkButton>
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}
